import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { useWatchlistStore } from '../../state/watchlistStore';
import { useAuthStore } from '../../state/authStore';
import { getPublicNav, bottomNav, discoverTabs, researchTabs, portfolioTabs } from '../../nav/publicNav';

const discoverPaths = discoverTabs.map(t => t.path);
const researchPaths = researchTabs.map(t => t.path);
const portfolioPaths = portfolioTabs.map(t => t.path);

export default function NavRail() {
  const navigate = useNavigate();
  const location = useLocation();
  const userType = useAuthStore((s) => s.user?.userType || 'retail');
  const nav = getPublicNav(userType);
  const unreadInsightsCount = useWatchlistStore((s) =>
    (s.insights || []).reduce((count, i) => count + (i.isRead ? 0 : 1), 0)
  );

  const isActive = (item) => {
    const p = location.pathname;
    if (item.path === '/public/home') return p === '/public/home';
    if (item.label === 'Discover') return discoverPaths.some(dp => p === dp || p.startsWith(dp + '/'));
    if (item.label === 'Research') return researchPaths.some(rp => p === rp || p.startsWith(rp + '/'));
    if (item.label === 'Portfolio') return portfolioPaths.some(pp => p === pp || p.startsWith(pp + '/'));
    return p.startsWith(item.path);
  };

  const renderItem = (item) => {
    const Icon = item.icon;
    const active = isActive(item);

    return (
      <TooltipProvider key={item.path} delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => navigate(item.path)}
              aria-label={item.label}
              aria-current={active ? 'page' : undefined}
              className={`relative flex flex-col items-center gap-0.5 w-full py-2 rounded-xl transition-all duration-150 ${
                active
                  ? 'bg-emerald-500/12 text-emerald-600 dark:text-emerald-400'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary/80'
              }`}
            >
              <Icon className="w-5 h-5" strokeWidth={active ? 2.2 : 1.6} />
              <span className={`text-[9px] leading-none ${active ? 'font-semibold' : 'font-medium'}`}>
                {item.label}
              </span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" className="text-xs font-medium">
            {item.label}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return (
    <>
      {/* Desktop: vertical rail */}
      <aside className="hidden md:flex fixed left-0 top-12 h-[calc(100vh-3rem)] w-16 z-30 flex-col items-center py-3 px-1.5 border-r border-sidebar-border bg-sidebar">
        <nav className="flex flex-col items-center gap-1 flex-1 w-full">
          {nav.map(renderItem)}
        </nav>
        <div className="flex flex-col items-center gap-1 w-full pt-2 border-t border-sidebar-border">
          {bottomNav.map(renderItem)}
        </div>
      </aside>

      {/* Mobile: bottom tab bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 h-14 bg-background/90 backdrop-blur-xl border-t border-border flex flex-wrap items-center justify-around gap-y-1 px-1 py-1 min-h-14" aria-label="Primary">
        {nav.map((item) => {
          const Icon = item.icon;
          const active = isActive(item);
          return (
            <button
              type="button"
              key={item.path}
              onClick={() => navigate(item.path)}
              aria-label={item.label}
              aria-current={active ? 'page' : undefined}
              className={`relative flex flex-col items-center gap-0.5 px-1.5 py-1 rounded-lg transition-colors min-w-0 flex-1 basis-[22%] max-w-[20%] ${
                active ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'
              }`}
            >
              <Icon className="w-5 h-5" strokeWidth={active ? 2.2 : 1.8} />
              <span className={`text-[10px] ${active ? 'font-semibold' : 'font-medium'}`}>{item.label}</span>
            </button>
          );
        })}
      </nav>
    </>
  );
}
